import { messages, type LocaleCode } from '../locales/messages';

/** Map route path → message key for nicer page-load subtitles */
const PATH_TITLE_KEYS: Array<{ test: RegExp; key: string }> = [
  { test: /^\/$/, key: 'nav.dashboard' },
  { test: /^\/quick-journal/, key: 'nav.quickJournal' },
  { test: /^\/journals\/close-wizard/, key: 'nav.closeWizard' },
  { test: /^\/journals\/entry/, key: 'nav.journals' },
  { test: /^\/journals\/pembetulan/, key: 'nav.journals' },
  { test: /^\/journals\/lookup/, key: 'nav.lookupRef' },
  { test: /^\/journals/, key: 'nav.journals' },
  { test: /^\/reconciliation/, key: 'nav.reconciliation' },
  { test: /^\/statements/, key: 'nav.statements' },
  { test: /^\/import/, key: 'nav.import' },
  { test: /^\/downloads/, key: 'nav.downloadReports' },
  { test: /^\/reports\/lk/, key: 'nav.financialStatements' },
  { test: /^\/reports\/omset/, key: 'nav.omsetYearly' },
  { test: /^\/reports/, key: 'nav.reportsHub' },
  { test: /^\/tax\/spt/, key: 'nav.sptMasa' },
  { test: /^\/tax\/fiscal/, key: 'nav.fiscal' },
  { test: /^\/tax\/withhold/, key: 'nav.withholdings' },
  { test: /^\/tax\/ssp/, key: 'nav.ssp' },
  { test: /^\/tax/, key: 'nav.taxHub' },
  { test: /^\/companies/, key: 'nav.companies' },
  { test: /^\/accounts/, key: 'nav.accounts' },
  { test: /^\/users/, key: 'nav.users' },
  { test: /^\/backup/, key: 'nav.backup' },
  { test: /^\/audit/, key: 'nav.audit' },
  { test: /^\/control\/quality/, key: 'nav.quality' },
  { test: /^\/control\/close/, key: 'nav.closeChecklist' },
  { test: /^\/auth\/login/, key: 'auth.signIn' },
];

function msg(locale: LocaleCode, key: string) {
  const table = messages[locale] || messages.id;
  return (table as Record<string, string>)[key] || (messages.id as Record<string, string>)[key] || key;
}

function pageLabel(locale: LocaleCode, path: string) {
  for (const row of PATH_TITLE_KEYS) {
    if (row.test.test(path)) return msg(locale, row.key);
  }
  return path.replace(/^\//, '') || msg(locale, 'nav.dashboard');
}

export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return;

  const router = useRouter();
  const { beginPageLoad, endPageLoad, state } = useAppSwitchOverlay();
  const locale = useState<LocaleCode>('app-locale', () => 'id');

  let pending = 0;
  let closeScheduled = false;

  const loc = () => (locale.value === 'en' ? 'en' : 'id');

  function startOverlay(opts: Parameters<typeof beginPageLoad>[0]) {
    beginPageLoad(opts);
    pending += 1;
    closeScheduled = false;
  }

  function scheduleClose() {
    if (pending <= 0 || closeScheduled) return;
    closeScheduled = true;
    requestAnimationFrame(() => {
      setTimeout(() => {
        endPageLoad();
        pending = Math.max(0, pending - 1);
        closeScheduled = false;
      }, 70);
    });
  }

  // Hard reload / first access
  startOverlay({
    kind: 'boot',
    title: msg(loc(), 'switch.bootTitle'),
    subtitle: msg(loc(), 'switch.bootSub'),
  });

  router.beforeEach((to, from) => {
    const isFirst = !from.matched.length;
    if (to.path === from.path && !isFirst) return;
    if (isFirst) return; // already showing boot

    const L = loc();
    const dest = pageLabel(L, to.path);
    const toAuth = /^\/auth\//i.test(to.path);
    const fromAuth = /^\/auth\//i.test(from.path);

    const enteringApp = fromAuth && !toAuth;
    startOverlay({
      kind: enteringApp ? 'auth' : 'page',
      title: enteringApp ? msg(L, 'switch.authTitle') : msg(L, 'switch.pageTitle'),
      subtitle: enteringApp
        ? msg(L, 'switch.authSub')
        : `${msg(L, 'switch.pageTo')} ${dest} · ${msg(L, 'switch.pageSub')}`,
      minMs: enteringApp ? 1200 : undefined,
      steps: enteringApp
        ? [
            { key: 'locale', label: msg(L, 'switch.authStepLocale') },
            { key: 'theme', label: msg(L, 'switch.authStepTheme') },
            { key: 'auth', label: msg(L, 'switch.authStepAuth') },
            { key: 'dash', label: msg(L, 'switch.authStepDash') },
          ]
        : undefined,
    });
  });

  router.afterEach(() => scheduleClose());
  nuxtApp.hook('page:finish', () => scheduleClose());

  nuxtApp.hook('app:mounted', () => {
    if (state.value.open && (state.value.kind === 'boot' || pending > 0)) {
      setTimeout(() => scheduleClose(), 160);
    }
  });

  router.onError(() => {
    endPageLoad();
    pending = 0;
    closeScheduled = false;
  });
});
